import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
interface AttentionItem {
  id: string;
  stream: string;
  streamId?: string;
  task: string;
  nodeId?: string;
  reason: string;
  reasonType: 'decision' | 'error' | 'input';
  timestamp: string;
}
interface AttentionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  items: AttentionItem[];
  // ATTENTION EXPERIMENT: Navigation callback
  onNavigateToItem?: (streamId: string, nodeId: string) => void;
}
export function AttentionPanel({
  isOpen,
  onClose,
  items,
  onNavigateToItem
}: AttentionPanelProps) {
  return <AnimatePresence>
      {isOpen && <>
        {/* Backdrop for dismissing panel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 top-16 bg-black/20 z-10"
        />
        <motion.div initial={{
      x: '100%',
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: '100%',
      opacity: 0
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="fixed right-0 top-16 bottom-0 w-[360px] bg-surface-900 border-l border-surface-600 shadow-2xl z-20 flex flex-col">
          <div className="p-5 border-b border-surface-600 flex items-center justify-between bg-surface-900/95 backdrop-blur">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-text-primary">Attention Required</h2>
              <span className="px-2 py-0.5 rounded-full bg-warning-500/20 text-warning-400 text-xs font-medium">
                {items.length}
              </span>
            </div>
            <button onClick={onClose} className="text-text-muted hover:text-text-primary">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {items.map(item => <div
                key={item.id}
                className="bg-surface-800/50 border border-surface-500 rounded-lg p-4 hover:border-primary-500/50 transition-colors group cursor-pointer"
                onClick={() => {
                  // ATTENTION EXPERIMENT: Navigate to node when clicking item
                  if (onNavigateToItem && item.streamId && item.nodeId) {
                    onNavigateToItem(item.streamId, item.nodeId);
                  }
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {item.reasonType === 'error' && <AlertCircle className="w-4 h-4 text-danger-500" />}
                    {item.reasonType === 'decision' && <AlertTriangle className="w-4 h-4 text-warning-500" />}
                    {item.reasonType === 'input' && <AlertCircle className="w-4 h-4 text-primary-500" />}
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded
                      ${item.reasonType === 'error' ? 'bg-danger-500/10 text-danger-400' : item.reasonType === 'decision' ? 'bg-warning-500/10 text-warning-400' : 'bg-primary-500/10 text-primary-400'}`}>
                      {item.reason}
                    </span>
                  </div>
                  <span className="text-[10px] text-text-muted">
                    {item.timestamp}
                  </span>
                </div>

                <h3 className="text-sm font-medium text-text-primary mb-1">
                  {item.task}
                </h3>
                <p className="text-xs text-text-secondary mb-3">in {item.stream}</p>

                {/* ATTENTION EXPERIMENT: Show navigation hint */}
                {item.streamId && item.nodeId && (
                  <div className="flex items-center gap-1 text-xs text-primary-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight className="w-3 h-3" />
                    <span>Jump to task</span>
                  </div>
                )}
              </div>)}

            {items.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-success-500/10 flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-6 h-6 text-success-500" />
                </div>
                <h3 className="text-sm font-medium text-text-primary">
                  All caught up!
                </h3>
                <p className="text-xs text-text-muted mt-1">
                  No items require your attention.
                </p>
              </div>}
          </div>
        </motion.div>
      </>}
    </AnimatePresence>;
}